/* cart-drawer.js */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if ((event.code || "").toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden", "cart-drawer-open");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden", "cart-drawer-open");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling?.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner?.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty");
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const newHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );

      sectionElement.innerHTML = newHTML;

      // ✅ re-hydrate after replacing the drawer markup
      if (section.id === "cart-drawer") {
        const cdi = sectionElement.querySelector("cart-drawer-items");
        if (cdi) customElements.upgrade(cdi);
      }
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

// Before the browser freezes this page into bfcache, close any open cart
// drawer so the snapshot starts in the closed state. Prevents stale
// overflow-hidden / cart-drawer-open body classes surviving on restore.
// Safety net: pageshow catches any restore where pagehide did not clean up,
// or where the body class is stale but the drawer element lost its active class.
(function () {
  function getDrawer() {
    return document.querySelector("cart-drawer");
  }

  window.addEventListener("pagehide", function (event) {
    if (!event.persisted) return;
    var drawer = getDrawer();
    if (drawer && drawer.classList.contains("active")) {
      drawer.close();
    }
  });

  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    var drawer = getDrawer();
    if (!drawer) return;
    if (
      drawer.classList.contains("active") ||
      document.body.classList.contains("cart-drawer-open")
    ) {
      drawer.close();
    }
  });
})();

/* Wait until CartItems (from cart.js) is available before defining cart-drawer-items */
(function defineCartDrawerItemsWhenReady() {
  const Base = window.CartItems || customElements.get("cart-items");
  if (!Base) {
    setTimeout(defineCartDrawerItemsWhenReady, 0);
    return;
  }

  class CartDrawerItems extends Base {
    getSectionsToRender() {
      return [
        { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
})();
